import {
  Button,
  CenteredSpinner,
  Col,
  Flex,
  FormContainer,
  Modal,
  notify,
  Row,
  Text,
  Title,
} from '@wyz/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { isEmpty } from 'lodash-es';
import { useRef } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';
import { CheckboxField, TextField } from '~/common/components';
import { Trans, useI18n } from '~/common/context';
import { ROLES } from '~/common/helpers';
import {
  useAccessControl,
  usePricingPolicyErrorTranslation,
} from '~/common/hooks';
import { useManageQuoteSetting, useManageQuotes } from '~/purchase/api';
import { QuoteContactForm } from '~/purchase/components';
import { QuoteHelpers } from '~/purchase/helpers';
import {
  IQuote,
  IQuoteContact,
  IQuoteErrorResponse,
  IQuoteSetting,
} from '~/purchase/types';
import { generateQuoteContactSchema } from '~/purchase/schemas';

interface QuoteRecipientModalProps {
  quote: IQuote;
  onClose: () => void;
}

interface RecipientFormProps {
  quote: IQuote;
  setting: IQuoteSetting;
  onClose: () => void;
}

const RecipientForm = ({ quote, setting, onClose }: RecipientFormProps) => {
  const { t } = useI18n();
  const acl = useAccessControl();
  const { resolveErrorKey } = usePricingPolicyErrorTranslation();

  // Both buttons submit so that validation runs; this records which one was pressed.
  const sendEmailRef = useRef(false);

  const [frontDimension, rearDimension] =
    QuoteHelpers.getQuoteDimensions(quote);
  // Two dimensions mean two axles, and only then are they labelled front and rear.
  const hasRearDimension = !isEmpty(rearDimension);
  // The vehicle fields stay on one line, whatever the label length: two or three even columns.
  const vehicleColumnSize = hasRearDimension ? 4 : 6;

  const methods = useForm<{
    contact: IQuoteContact;
    frontDimension: string;
    rearDimension: string;
  }>({
    defaultValues: {
      contact: QuoteHelpers.toContact(quote),
      // A services-only quote has no dimension at all: keep the fields controlled all the same.
      frontDimension: frontDimension ?? '',
      rearDimension: rearDimension ?? '',
    },
    resolver: zodResolver(
      z.object({
        contact: generateQuoteContactSchema(setting),
        // Shown only: the submit handler sends `form.contact` and nothing else.
        frontDimension: z.string(),
        rearDimension: z.string(),
      }),
    ),
  });

  const [watchedEmail, watchedGdprAccepted] = useWatch({
    name: ['contact.email', 'contact.gdprAccepted'],
    control: methods.control,
  });

  const manageQuotes = useManageQuotes({
    editContact: { enabled: true },
    resendMail: { enabled: true },
  });

  const isSubmitting =
    manageQuotes.editContact.isLoading || manageQuotes.resendMail.isLoading;

  // A draft is never sendable, whatever the recipient data.
  const allowSendMail =
    quote.status !== 'DRAFT' &&
    !isEmpty(watchedEmail) &&
    watchedGdprAccepted &&
    acl.isGranted([ROLES.ROLE_QUOTE_SEND_MAIL]);

  return (
    <FormContainer
      methods={methods}
      submitHandler={async (form) => {
        const sendEmail = sendEmailRef.current;
        const variables = {
          customerId: quote.customerId,
          reference: quote.reference,
        };

        // Saving the contact and sending the quote are two separate responsibilities, so they are
        // two separate calls — and they can fail independently. Each outcome gets its own message.
        try {
          await manageQuotes.editContact.mutateAsync({
            ...variables,
            body: { contact: form.contact },
          });
        } catch (error) {
          // Nothing was saved: keep the modal open so the user does not lose their edit.
          notify.error(
            <Trans
              i18nKey={resolveErrorKey((error as IQuoteErrorResponse).message)}
            />,
            { id: 'toast-error-quote-edit-recipient' },
          );

          return;
        }

        if (!sendEmail) {
          notify.success(<Trans i18nKey='app.common.messages.success' />, {
            id: 'toast-success-quote-edit-recipient',
          });
          onClose();

          return;
        }

        try {
          await manageQuotes.resendMail.mutateAsync(variables);
          notify.success(
            <Trans i18nKey='app.purchase.quote.history.recipient_modal.sent_success' />,
            { id: 'toast-success-quote-edit-recipient' },
          );
        } catch {
          // The contact IS saved — say so, rather than implying the whole thing failed.
          notify.error(
            <Trans i18nKey='app.purchase.quote.history.recipient_modal.saved_not_sent' />,
            { id: 'toast-error-quote-recipient-not-sent' },
          );
        }

        onClose();
      }}
    >
      <Title heading='lg' weight='bold' color='secondary'>
        <Trans i18nKey='app.purchase.quote.history.recipient_modal.vehicle' />
      </Title>
      <Row>
        <Col xs={vehicleColumnSize}>
          <TextField
            name='contact.registration'
            label={
              <Trans i18nKey='app.purchase.quote_cart_page.contact.matriculation.label' />
            }
            placeholder={t(
              'app.purchase.quote_cart_page.contact.matriculation.placeholder',
            )}
            required={setting.vehicleRegistrationRequired}
          />
        </Col>
        <Col xs={vehicleColumnSize}>
          {/* Derived from the quote lines: never editable from here. */}
          <TextField
            name='frontDimension'
            label={
              <Trans
                i18nKey={
                  hasRearDimension
                    ? 'app.purchase.quote.history.recipient_modal.front_dimension'
                    : 'app.common.product.dimension'
                }
              />
            }
            disabled
          />
        </Col>
        {/* A single-dimension quote has no axle to tell apart: the field above carries it alone. */}
        {hasRearDimension && (
          <Col xs={vehicleColumnSize}>
            <TextField
              name='rearDimension'
              label={
                <Trans i18nKey='app.purchase.quote.history.recipient_modal.rear_dimension' />
              }
              disabled
            />
          </Col>
        )}
      </Row>
      {/* The plate lives in the vehicle section above, not among the identity fields. */}
      <QuoteContactForm settings={setting} withImmatriculation={false} />
      <CheckboxField
        name='contact.gdprAccepted'
        label={
          setting.resolvedGdpr1 ||
          t('app.purchase.quote_cart_page.contact.accept_data')
        }
      />
      <Flex className='space-x-2 mt-3'>
        <Button
          id='cancel-quote-recipient'
          variant='secondary'
          disabled={isSubmitting}
          onClick={onClose}
          fullWidth
        >
          <Trans i18nKey='app.common.button.cancel' />
        </Button>
        <Button
          id='save-quote-recipient'
          variant='secondary'
          type='submit'
          isLoading={isSubmitting}
          onClick={() => {
            sendEmailRef.current = false;
          }}
          fullWidth
        >
          <Trans i18nKey='app.purchase.quote_cart_page.form.save' />
        </Button>
        <Button
          id='save-and-send-quote-recipient'
          variant='primary'
          type='submit'
          disabled={!allowSendMail}
          isLoading={isSubmitting}
          onClick={() => {
            sendEmailRef.current = true;
          }}
          fullWidth
        >
          <Trans i18nKey='app.purchase.quote_cart_page.form.save_and_send' />
        </Button>
      </Flex>
    </FormContainer>
  );
};

export const QuoteRecipientModal = ({
  quote,
  onClose,
}: QuoteRecipientModalProps) => {
  const quoteSettingManagement = useManageQuoteSetting({
    get: { enabled: true },
  });

  return (
    <Modal.Container open={true} onClose={onClose} size='lg' backdrop='static'>
      <Modal.Header>
        <Modal.Title>
          <Trans i18nKey='app.purchase.quote.history.recipient_modal.title' />
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {quoteSettingManagement.get.isSuccess && (
          <RecipientForm
            quote={quote}
            setting={quoteSettingManagement.get.data.data}
            onClose={onClose}
          />
        )}
        {/* The form shape depends on the settings, so there is nothing to render without them. */}
        {quoteSettingManagement.get.isError && (
          <Text color='danger'>
            <Trans i18nKey='app.common.select_field.options_error' />
          </Text>
        )}
        {!quoteSettingManagement.get.isSettled && <CenteredSpinner />}
      </Modal.Body>
    </Modal.Container>
  );
};
